import {
  LEAD_EVENT_TYPES,
  MAX_CRM_CONNECTIONS,
  MAX_SLACK_CONNECTIONS,
  MAX_WEBHOOK_ENDPOINTS,
  WEBHOOK_EVENT_LABELS,
  WEBHOOK_EVENT_TYPES,
  WEBHOOK_HEADERS,
  createWebhookEndpointBodySchema,
  type CrmConnection,
  type SlackConnection,
  type WebhookDelivery,
  type WebhookDeliveryStatus,
  type WebhookEndpoint,
  type WebhookEndpointWithSecret,
  type WebhookEventType,
  type WebhookTestResult,
} from '@groundly/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  CirclePause,
  CircleX,
  Clock,
  Search,
  type LucideIcon,
} from 'lucide-react';
import { useContext, useId, useMemo, useState, type FormEvent, type ReactNode } from 'react';

import {
  errorMessage,
  useCreateWebhookEndpoint,
  useDeleteHubSpotConnection,
  useDeleteSalesforceConnection,
  useDeleteSlackConnection,
  useDeleteWebhookEndpoint,
  useHubSpotConnections,
  useSalesforceConnections,
  useRedeliverWebhook,
  useRotateWebhookSecret,
  useSlackConnections,
  useStartHubSpotInstall,
  useStartSalesforceInstall,
  useStartSlackInstall,
  useTestHubSpotConnection,
  useTestSalesforceConnection,
  useTestSlackConnection,
  useTestWebhookEndpoint,
  useUpdateHubSpotConnection,
  useUpdateSalesforceConnection,
  useUpdateSlackConnection,
  useUpdateZohoConnection,
  useDeleteZohoConnection,
  useStartZohoInstall,
  useTestZohoConnection,
  useZohoConnections,
  useUpdateWebhookEndpoint,
  useWebhookDeliveries,
  useWebhookEndpoints,
} from '../../../../api/integrations.js';
import { usePermissions } from '../../../../api/me.js';
import { Button } from '../../../../components/Button.js';
import { DangerAlert } from '../../../../components/DangerAlert.js';
import { Dialog } from '../../../../components/Dialog.js';
import { InfoAlert } from '../../../../components/InfoAlert.js';
import styles from '../../../../components/integrations.module.css';
import { PageHeading } from '../../../../components/PageHeading.js';
import { Pagination } from '../../../../components/Pagination.js';
import { LoadingArea } from '../../../../components/PlaceholderPage.js';
import { PermissionDeniedPage, WebsiteNotFoundPage } from '../../../../components/SectionStates.js';
import { StatusBadge } from '../../../../components/StatusBadge.js';
import { TextField } from '../../../../components/TextField.js';
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  INTEGRATIONS,
  WEBHOOK_GUIDES,
  integrationById,
  isIntegrationId,
  toolForUrl,
  type Integration,
  type IntegrationCategory,
  type IntegrationId,
  type OpenIntegration,
  type WebhookTool,
} from '../../../../integrations/catalog.js';
import { IntegrationLogo } from '../../../../integrations/IntegrationLogo.js';
import { WebsiteStatusContext } from '../../../../components/WebsiteSection.js';
import { formatFullDateTime } from '../../../../conversations/display.js';

/**
 * `/websites/:websiteId/integrations`: where the website's leads are sent
 * (`features/integrations`). Without `?integration=` it is the catalog: what's
 * connected now, then every tool, by category, with its logo. With
 * `?integration=slack|webhooks|zapier|make|n8n` it is that tool's own view,
 * where it is connected and managed. Needs the integrations permission; adding
 * and testing need a plan with integrations, which the server checks too.
 */
export const Route = createFileRoute('/_app/websites/$websiteId/integrations')({
  validateSearch: (search: Record<string, unknown>): { integration?: IntegrationId } =>
    isIntegrationId(search.integration) ? { integration: search.integration } : {},
  component: IntegrationsRoute,
});

const TITLE = 'Integrations';

function IntegrationsRoute() {
  const permissions = usePermissions();
  const websiteStatus = useContext(WebsiteStatusContext);

  if (!permissions.includes('integrations')) {
    return <PermissionDeniedPage title={TITLE} permission="integrations" />;
  }
  if (websiteStatus === 'not-found') return <WebsiteNotFoundPage title={TITLE} />;
  return <IntegrationsPage />;
}

function IntegrationsPage() {
  const { websiteId } = Route.useParams();
  const { integration } = Route.useSearch();
  // Keyed so a tool's view starts fresh (and its heading takes focus) each time.
  return integration === undefined ? (
    <Overview key="overview" websiteId={websiteId} />
  ) : (
    <IntegrationView key={integration} websiteId={websiteId} id={integration} />
  );
}

const STATUS: Record<
  'on' | 'off' | 'failing' | 'revoked',
  { label: string; tone: 'success' | 'neutral' | 'danger'; icon: LucideIcon }
> = {
  on: { label: 'On', tone: 'success', icon: CircleCheck },
  off: { label: 'Off', tone: 'neutral', icon: CirclePause },
  failing: { label: 'Turned off: kept failing', tone: 'danger', icon: CircleX },
  revoked: { label: 'Connection revoked', tone: 'danger', icon: CircleX },
};

function statusOf(item: { isEnabled: boolean; disabledReason: 'failing' | 'revoked' | null }) {
  if (item.isEnabled) return STATUS.on;
  return item.disabledReason === null ? STATUS.off : STATUS[item.disabledReason];
}

/** Shown on every view while the website's plan has no integrations. */
function PlanNotice() {
  return (
    <div className={styles.notice}>
      <InfoAlert title="Integrations come with paid plans">
        This website is on a plan without integrations, so no leads are sent.{' '}
        <Link to="/account/billing">Change its plan in Billing</Link> to send leads to Slack or a
        webhook.
      </InfoAlert>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The catalog
// ---------------------------------------------------------------------------

/** One live connection, whatever its tool, as the Connected list shows it. */
interface Connection {
  key: string;
  integration: OpenIntegration;
  /** What the leads go to: a channel, an address, a description. */
  target: string;
  /** Set when `target` is a literal address, so it is set in mono. */
  isAddress: boolean;
  status: (typeof STATUS)[keyof typeof STATUS];
  last: string;
}

function Overview({ websiteId }: { websiteId: string }) {
  const endpoints = useWebhookEndpoints(websiteId);
  const slack = useSlackConnections(websiteId);
  const hubspot = useHubSpotConnections(websiteId);
  const salesforce = useSalesforceConnections(websiteId);
  const zoho = useZohoConnections(websiteId);
  const data = endpoints.data;

  return (
    <>
      <header className={styles.header}>
        <PageHeading documentTitle={TITLE}>{TITLE}</PageHeading>
        <p className={styles.caption}>Send this website&apos;s leads to the tools you use.</p>
      </header>

      {endpoints.isError ? (
        <DangerAlert
          title="Integrations didn't load"
          action={<Button onClick={() => void endpoints.refetch()}>Try again</Button>}
        >
          Groundly couldn&apos;t reach the server. Check your connection and try again.
        </DangerAlert>
      ) : endpoints.isPending || data === undefined ? (
        <LoadingArea />
      ) : (
        <>
          {data.integrationsAllowed ? null : <PlanNotice />}
          <ConnectedSection
            websiteId={websiteId}
            webhooks={data.items}
            slack={slack.data?.items}
            slackFailed={slack.isError}
            onRetrySlack={() => void slack.refetch()}
            hubspot={hubspot.data?.items}
            hubspotFailed={hubspot.isError}
            onRetryHubSpot={() => void hubspot.refetch()}
            salesforce={salesforce.data?.items}
            salesforceFailed={salesforce.isError}
            onRetrySalesforce={() => void salesforce.refetch()}
            zoho={zoho.data?.items}
            zohoFailed={zoho.isError}
            onRetryZoho={() => void zoho.refetch()}
          />
          <Catalog
            websiteId={websiteId}
            webhooks={data.items}
            slackCount={slack.data?.items.length ?? 0}
          />
        </>
      )}
    </>
  );
}

function lastLabel(
  lastDelivery: { createdAt: string; status: WebhookDeliveryStatus } | null,
  verb: 'posted' | 'sent',
) {
  if (lastDelivery === null) return verb === 'posted' ? 'Nothing posted yet' : 'Nothing sent yet';
  return `Last ${verb} ${formatFullDateTime(lastDelivery.createdAt)}: ${DELIVERY_LABELS[lastDelivery.status].toLowerCase()}`;
}

/** A webhook's tool, when its address shows it is one (Zapier, Make, n8n). */
function webhookIntegration(endpoint: WebhookEndpoint): OpenIntegration {
  return integrationById(toolForUrl(endpoint.url) ?? 'webhooks');
}

function ConnectedSection({
  websiteId,
  webhooks,
  slack,
  slackFailed,
  onRetrySlack,
  hubspot,
  hubspotFailed,
  onRetryHubSpot,
  salesforce,
  salesforceFailed,
  onRetrySalesforce,
  zoho,
  zohoFailed,
  onRetryZoho,
}: {
  websiteId: string;
  webhooks: readonly WebhookEndpoint[];
  slack: readonly SlackConnection[] | undefined;
  slackFailed: boolean;
  onRetrySlack: () => void;
  hubspot: readonly CrmConnection[] | undefined;
  hubspotFailed: boolean;
  onRetryHubSpot: () => void;
  salesforce: readonly CrmConnection[] | undefined;
  salesforceFailed: boolean;
  onRetrySalesforce: () => void;
  zoho: readonly CrmConnection[] | undefined;
  zohoFailed: boolean;
  onRetryZoho: () => void;
}) {
  const connections: Connection[] = [
    ...(slack ?? []).map((c) => ({
      key: `slack-${c.id}`,
      integration: integrationById('slack'),
      target: `#${c.channelName} in ${c.teamName}`,
      isAddress: false,
      status: statusOf(c),
      last: lastLabel(c.lastDelivery, 'posted'),
    })),
    ...(hubspot ?? []).map((c) => ({
      key: `hubspot-${c.id}`,
      integration: integrationById('hubspot'),
      target: c.accountName || 'HubSpot',
      isAddress: false,
      status: statusOf(c),
      last: lastLabel(c.lastDelivery, 'sent'),
    })),
    ...(salesforce ?? []).map((c) => ({
      key: `salesforce-${c.id}`,
      integration: integrationById('salesforce'),
      target: c.accountName || 'Salesforce',
      isAddress: false,
      status: statusOf(c),
      last: lastLabel(c.lastDelivery, 'sent'),
    })),
    ...(zoho ?? []).map((c) => ({
      key: `zoho-${c.id}`,
      integration: integrationById('zoho'),
      target: c.accountName || 'Zoho CRM',
      isAddress: false,
      status: statusOf(c),
      last: lastLabel(c.lastDelivery, 'sent'),
    })),
    ...webhooks.map((e) => ({
      key: `webhook-${e.id}`,
      integration: webhookIntegration(e),
      target: e.description ?? e.url,
      isAddress: e.description === null,
      status: statusOf(e),
      last: lastLabel(e.lastDelivery, 'sent'),
    })),
  ];
  // Anything that stopped working comes first: it is what needs the user.
  const needsAttention = connections.filter((c) => c.status.tone === 'danger').length;
  connections.sort(
    (a, b) => Number(b.status.tone === 'danger') - Number(a.status.tone === 'danger'),
  );

  return (
    <section className={styles.section} aria-labelledby="connected-heading">
      <div className={styles.sectionHead}>
        <div>
          <h2 id="connected-heading" className={styles.sectionTitle}>
            Connected
          </h2>
          <p className={styles.sectionText}>
            {connections.length === 0
              ? 'Leads go nowhere yet. Connect a tool below and each new lead is sent as it arrives.'
              : `${connections.length} ${connections.length === 1 ? 'place gets' : 'places get'} this website's leads${needsAttention > 0 ? `; ${needsAttention} ${needsAttention === 1 ? 'needs' : 'need'} attention` : ''}.`}
          </p>
        </div>
      </div>

      {slackFailed ? (
        <p className={styles.error} role="alert">
          <CircleAlert aria-hidden="true" className={styles.errorIcon} strokeWidth={1.75} />
          Slack channels didn&apos;t load.{' '}
          <Button small onClick={onRetrySlack}>
            Try again
          </Button>
        </p>
      ) : null}
      {hubspotFailed ? (
        <p className={styles.error} role="alert">
          <CircleAlert aria-hidden="true" className={styles.errorIcon} strokeWidth={1.75} />
          HubSpot connections didn&apos;t load.{' '}
          <Button small onClick={onRetryHubSpot}>
            Try again
          </Button>
        </p>
      ) : null}
      {salesforceFailed ? (
        <p className={styles.error} role="alert">
          <CircleAlert aria-hidden="true" className={styles.errorIcon} strokeWidth={1.75} />
          Salesforce connections didn&apos;t load.{' '}
          <Button small onClick={onRetrySalesforce}>
            Try again
          </Button>
        </p>
      ) : null}
      {zohoFailed ? (
        <p className={styles.error} role="alert">
          <CircleAlert aria-hidden="true" className={styles.errorIcon} strokeWidth={1.75} />
          Zoho CRM connections didn&apos;t load.{' '}
          <Button small onClick={onRetryZoho}>
            Try again
          </Button>
        </p>
      ) : null}

      {connections.length === 0 ? null : (
        <ul className={styles.connected}>
          {connections.map((c) => (
            <li key={c.key}>
              <Link
                to="/websites/$websiteId/integrations"
                params={{ websiteId }}
                search={{
                  integration:
                    c.integration.id === 'slack' ||
                    c.integration.id === 'hubspot' ||
                    c.integration.id === 'salesforce' ||
                    c.integration.id === 'zoho'
                      ? c.integration.id
                      : 'webhooks',
                }}
                className={styles.connectedRow}
              >
                <IntegrationLogo logo={c.integration.logo} size="sm" />
                <span className={styles.connectedMain}>
                  <span className={styles.connectedName}>{c.integration.name}</span>
                  <span className={c.isAddress ? styles.connectedAddress : styles.connectedTarget}>
                    {c.target}
                  </span>
                </span>
                <span className={styles.connectedLast}>{c.last}</span>
                <StatusBadge tone={c.status.tone} icon={c.status.icon}>
                  {c.status.label}
                </StatusBadge>
                <ChevronRight aria-hidden="true" className={styles.chevron} strokeWidth={1.75} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

type CategoryFilter = IntegrationCategory | 'all';

const AVAILABILITY_RANK: Record<Integration['availability'], number> = {
  native: 0,
  webhook: 1,
  soon: 2,
};

function matches(integration: Integration, query: string) {
  if (query === '') return true;
  const haystack =
    `${integration.name} ${CATEGORY_LABELS[integration.category]} ${integration.summary}`.toLowerCase();
  return query
    .split(/\s+/)
    .filter((word) => word !== '')
    .every((word) => haystack.includes(word));
}

function Catalog({
  websiteId,
  webhooks,
  slackCount,
}: {
  websiteId: string;
  webhooks: readonly WebhookEndpoint[];
  slackCount: number;
}) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<CategoryFilter>('all');
  const searchId = useId();
  const normalized = query.trim().toLowerCase();

  // How many connections each tool has, for its "2 connected" badge.
  const counts = useMemo(() => {
    const found: Partial<Record<IntegrationId, number>> = { slack: slackCount, webhooks: 0 };
    for (const endpoint of webhooks) {
      const id = webhookIntegration(endpoint).id;
      found[id] = (found[id] ?? 0) + 1;
    }
    return found;
  }, [webhooks, slackCount]);

  // What works today first, then through a webhook, then what's coming; by
  // category within each, so the grid stays one dense block as tools are added.
  const shown = INTEGRATIONS.filter(
    (i) => (category === 'all' || i.category === category) && matches(i, normalized),
  ).sort(
    (a, b) =>
      AVAILABILITY_RANK[a.availability] - AVAILABILITY_RANK[b.availability] ||
      CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category),
  );

  return (
    <section className={styles.section} aria-labelledby="catalog-heading">
      <div className={styles.sectionHead}>
        <div>
          <h2 id="catalog-heading" className={styles.sectionTitle}>
            All integrations
          </h2>
          <p className={styles.sectionText}>
            Choose a tool to connect it. Tools marked “Through a webhook” work today, with a short
            setup in that tool.
          </p>
        </div>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.search}>
          <label htmlFor={searchId} className={styles.visuallyHidden}>
            Find an integration
          </label>
          <Search aria-hidden="true" className={styles.searchIcon} strokeWidth={1.75} />
          <input
            id={searchId}
            type="search"
            className={styles.searchInput}
            placeholder="Find an integration"
            autoComplete="off"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setQuery('');
            }}
          />
        </div>
        <div className={styles.filters} role="group" aria-label="Category">
          {(['all', ...CATEGORY_ORDER] as const).map((c) => (
            <button
              key={c}
              type="button"
              className={styles.filter}
              aria-pressed={category === c}
              onClick={() => setCategory(c)}
            >
              {c === 'all' ? 'All' : CATEGORY_LABELS[c]}
            </button>
          ))}
        </div>
      </div>

      {shown.length === 0 ? (
        <div className={styles.empty} role="status">
          <h3>
            {normalized === ''
              ? `No ${CATEGORY_LABELS[category as IntegrationCategory]} integrations yet`
              : `No integrations match “${query.trim()}”`}
          </h3>
          <p>
            Any tool that can receive a webhook works today.{' '}
            <Link
              to="/websites/$websiteId/integrations"
              params={{ websiteId }}
              search={{ integration: 'webhooks' }}
            >
              Set up a webhook
            </Link>
            .
          </p>
        </div>
      ) : (
        <ul className={styles.grid} aria-label="Integrations">
          {shown.map((integration) => (
            <li key={integration.id} className={styles.cellItem}>
              <CatalogCell
                websiteId={websiteId}
                integration={integration}
                count={integration.availability === 'soon' ? 0 : (counts[integration.id] ?? 0)}
              />
            </li>
          ))}
        </ul>
      )}

      <p className={styles.footnote}>
        Don&apos;t see your tool? Anything that can receive a webhook works now, directly or through
        Zapier. Tell us which tool you&apos;d like next in <Link to="/support">Support</Link>.
      </p>
    </section>
  );
}

function CatalogCell({
  websiteId,
  integration,
  count,
}: {
  websiteId: string;
  integration: Integration;
  count: number;
}) {
  const body = (
    <>
      <IntegrationLogo logo={integration.logo} />
      <span className={styles.cellText}>
        <span className={styles.cellName}>{integration.name}</span>
        <span className={styles.cellSummary}>{integration.summary}</span>
        <span className={styles.cellFoot}>
          <span className={styles.cellCategory}>{CATEGORY_LABELS[integration.category]}</span>
          <CellState integration={integration} count={count} />
        </span>
      </span>
    </>
  );

  if (integration.availability === 'soon') {
    return <div className={`${styles.cell} ${styles.cellSoon}`}>{body}</div>;
  }
  return (
    <Link
      to="/websites/$websiteId/integrations"
      params={{ websiteId }}
      search={{ integration: integration.id }}
      className={styles.cell}
    >
      {body}
    </Link>
  );
}

function CellState({ integration, count }: { integration: Integration; count: number }) {
  if (integration.availability === 'soon') {
    return (
      <StatusBadge tone="neutral" icon={Clock}>
        Coming soon
      </StatusBadge>
    );
  }
  if (count > 0) {
    return (
      <StatusBadge tone="success" icon={CircleCheck}>
        {`${count} connected`}
      </StatusBadge>
    );
  }
  return (
    <span className={styles.cellAction}>
      {integration.availability === 'webhook' ? 'Through a webhook' : 'Connect'}
      <ChevronRight aria-hidden="true" className={styles.cellActionIcon} strokeWidth={1.75} />
    </span>
  );
}

// ---------------------------------------------------------------------------
// A tool's own view
// ---------------------------------------------------------------------------

const AVAILABILITY_LABELS = {
  native: 'Built into Groundly',
  webhook: 'Through a webhook',
} as const;

function IntegrationView({ websiteId, id }: { websiteId: string; id: IntegrationId }) {
  return (
    <>
      <Link
        to="/websites/$websiteId/integrations"
        params={{ websiteId }}
        search={{}}
        className={styles.back}
      >
        <ChevronLeft aria-hidden="true" strokeWidth={1.75} width={16} height={16} />
        All integrations
      </Link>
      {id === 'slack' ? (
        <SlackView websiteId={websiteId} />
      ) : id === 'hubspot' ? (
        <HubSpotView websiteId={websiteId} />
      ) : id === 'salesforce' ? (
        <SalesforceView websiteId={websiteId} />
      ) : id === 'zoho' ? (
        <ZohoView websiteId={websiteId} />
      ) : id === 'webhooks' ? (
        <WebhooksView websiteId={websiteId} />
      ) : (
        <WebhookToolView websiteId={websiteId} tool={id} />
      )}
    </>
  );
}

function ViewHeader({
  integration,
  description,
  action,
}: {
  integration: OpenIntegration;
  description: string;
  action?: ReactNode;
}) {
  return (
    <header className={styles.viewHeader}>
      <IntegrationLogo logo={integration.logo} size="lg" />
      <div className={styles.viewTitle}>
        <PageHeading documentTitle={`${integration.name} · ${TITLE}`}>
          {integration.name}
        </PageHeading>
        <p className={styles.viewKind}>
          {CATEGORY_LABELS[integration.category]} · {AVAILABILITY_LABELS[integration.availability]}
        </p>
        <p className={styles.viewDescription}>{description}</p>
      </div>
      {action === undefined || action === null ? null : (
        <div className={styles.viewAction}>{action}</div>
      )}
    </header>
  );
}

/** The facts beside a tool's connections: what's sent, limits, how it fails. */
function Facts({ items }: { items: readonly { term: string; detail: ReactNode }[] }) {
  return (
    <aside className={styles.facts} aria-label="How it works">
      <dl>
        {items.map((item) => (
          <div key={item.term} className={styles.fact}>
            <dt>{item.term}</dt>
            <dd>{item.detail}</dd>
          </div>
        ))}
      </dl>
    </aside>
  );
}

/**
 * Slack channels this website's leads are posted to. "Add to Slack" sends the
 * browser to Slack to pick a channel; Slack sends it back to
 * `/integrations/slack/callback`, which finishes the connection and returns
 * here.
 */
function SlackView({ websiteId }: { websiteId: string }) {
  const connections = useSlackConnections(websiteId);
  const start = useStartSlackInstall(websiteId);
  const data = connections.data;
  const atLimit = data !== undefined && data.items.length >= data.limit;
  // Waiting on Slack's page too: the browser is leaving.
  const isLeaving = start.isPending || start.isSuccess;

  return (
    <>
      <ViewHeader
        integration={integrationById('slack')}
        description="Groundly posts each new lead to the Slack channel you choose: who the visitor is, how to reach them, and a link to the conversation. It can post each new conversation too."
        action={
          data !== undefined && data.slackAvailable && data.integrationsAllowed && !atLimit ? (
            <Button variant="primary" onClick={() => start.mutate()} disabled={isLeaving}>
              {isLeaving ? 'Opening Slack…' : 'Add to Slack'}
            </Button>
          ) : null
        }
      />

      {data !== undefined && !data.integrationsAllowed ? <PlanNotice /> : null}

      {start.error === null ? null : (
        <p className={`${styles.error} ${styles.viewError}`} role="alert">
          <CircleAlert aria-hidden="true" className={styles.errorIcon} strokeWidth={1.75} />
          {errorMessage(start.error, "Slack didn't open. Check your connection and try again.")}
        </p>
      )}

      <div className={styles.viewBody}>
        <section className={styles.viewMain} aria-labelledby="slack-channels-heading">
          <h2 id="slack-channels-heading" className={styles.subTitle}>
            Channels
          </h2>
          {connections.isError ? (
            <p className={styles.error} role="alert">
              Slack channels didn&apos;t load.{' '}
              <Button small onClick={() => void connections.refetch()}>
                Try again
              </Button>
            </p>
          ) : data === undefined ? (
            <LoadingArea />
          ) : !data.slackAvailable && data.items.length === 0 ? (
            <p className={styles.muted}>Slack isn&apos;t set up on this Groundly yet.</p>
          ) : (
            <>
              {atLimit ? (
                <p className={styles.limit}>
                  A website can post to up to {MAX_SLACK_CONNECTIONS} Slack channels. Remove one to
                  add another.
                </p>
              ) : null}
              {data.items.length === 0 ? (
                <div className={styles.empty}>
                  <h3>No Slack channels yet</h3>
                  <p>
                    Add to Slack, pick a channel, and every new lead shows up where your team
                    already talks.
                  </p>
                </div>
              ) : (
                <ul className={styles.list}>
                  {data.items.map((connection) => (
                    <SlackRow
                      key={connection.id}
                      websiteId={websiteId}
                      connection={connection}
                      canSend={data.integrationsAllowed}
                    />
                  ))}
                </ul>
              )}
            </>
          )}
        </section>

        <Facts
          items={[
            {
              term: 'What’s posted',
              detail:
                'Who the visitor is, how to reach them, and a link to the conversation in Groundly.',
            },
            {
              term: 'When',
              detail:
                'As each lead arrives. New conversations too, if you tick them for a channel.',
            },
            {
              term: 'Limit',
              detail: `Up to ${MAX_SLACK_CONNECTIONS} channels per website.`,
            },
            {
              term: 'Removing Groundly',
              detail:
                'Removing a channel here stops its posts. To take Groundly out of your workspace, remove the app in Slack.',
            },
          ]}
        />
      </div>
    </>
  );
}

function HubSpotView({ websiteId }: { websiteId: string }) {
  const connections = useHubSpotConnections(websiteId);
  const start = useStartHubSpotInstall(websiteId);
  const data = connections.data;
  const atLimit = data !== undefined && data.items.length >= data.limit;
  const isLeaving = start.isPending || start.isSuccess;

  return (
    <>
      <ViewHeader
        integration={integrationById('hubspot')}
        description="Groundly creates or updates a HubSpot contact for each new lead: the visitor's name, email, phone, and a link to the conversation. If a contact with the same email already exists, it's updated."
        action={
          data !== undefined && data.crmAvailable && data.integrationsAllowed && !atLimit ? (
            <Button variant="primary" onClick={() => start.mutate()} disabled={isLeaving}>
              {isLeaving ? 'Opening HubSpot…' : 'Connect HubSpot'}
            </Button>
          ) : null
        }
      />

      {data !== undefined && !data.integrationsAllowed ? <PlanNotice /> : null}

      {start.error === null ? null : (
        <p className={`${styles.error} ${styles.viewError}`} role="alert">
          <CircleAlert aria-hidden="true" className={styles.errorIcon} strokeWidth={1.75} />
          {errorMessage(start.error, "HubSpot didn't open. Check your connection and try again.")}
        </p>
      )}

      <div className={styles.viewBody}>
        <section className={styles.viewMain} aria-labelledby="hubspot-connections-heading">
          <h2 id="hubspot-connections-heading" className={styles.subTitle}>
            Connections
          </h2>
          {connections.isError ? (
            <p className={styles.error} role="alert">
              HubSpot connections didn&apos;t load.{' '}
              <Button small onClick={() => void connections.refetch()}>
                Try again
              </Button>
            </p>
          ) : data === undefined ? (
            <LoadingArea />
          ) : !data.crmAvailable && data.items.length === 0 ? (
            <p className={styles.muted}>HubSpot isn&apos;t set up on this Groundly yet.</p>
          ) : (
            <>
              {atLimit ? (
                <p className={styles.limit}>
                  A website can have {MAX_CRM_CONNECTIONS} HubSpot connection. Remove it to connect
                  a different account.
                </p>
              ) : null}
              {data.items.length === 0 ? (
                <div className={styles.empty}>
                  <h3>No HubSpot connection yet</h3>
                  <p>Connect HubSpot and every new lead becomes a contact in your CRM.</p>
                </div>
              ) : (
                <ul className={styles.list}>
                  {data.items.map((connection) => (
                    <HubSpotRow
                      key={connection.id}
                      websiteId={websiteId}
                      connection={connection}
                      canSend={data.integrationsAllowed}
                    />
                  ))}
                </ul>
              )}
            </>
          )}
        </section>

        <Facts
          items={[
            {
              term: 'What’s synced',
              detail:
                'Name, email, phone, the visitor’s message, and a link to the Groundly conversation.',
            },
            {
              term: 'When',
              detail: 'As each lead arrives. New conversations too, if you tick them.',
            },
            {
              term: 'Matching',
              detail:
                'Groundly matches by email. If the contact already exists, it is updated; otherwise a new one is created.',
            },
            {
              term: 'Limit',
              detail: 'One HubSpot connection per website.',
            },
          ]}
        />
      </div>
    </>
  );
}

function HubSpotRow({
  websiteId,
  connection,
  canSend,
}: {
  websiteId: string;
  connection: CrmConnection;
  canSend: boolean;
}) {
  const update = useUpdateHubSpotConnection(websiteId, connection.id);
  const remove = useDeleteHubSpotConnection(websiteId, connection.id);
  const test = useTestHubSpotConnection(websiteId, connection.id);
  const [showDeliveries, setShowDeliveries] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const deliveriesId = useId();

  const status = statusOf(connection);
  const revoked = connection.disabledReason === 'revoked';
  const failure = update.error ?? test.error;

  return (
    <li className={styles.item}>
      <div className={styles.top}>
        <span className={styles.url}>
          {connection.accountName || 'HubSpot'}{' '}
          {connection.portalUrl ? (
            <a
              href={connection.portalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.muted}
            >
              Open in HubSpot
            </a>
          ) : null}
        </span>
        <StatusBadge tone={status.tone} icon={status.icon}>
          {status.label}
        </StatusBadge>
      </div>
      <p className={styles.meta}>
        <span>Syncs: {connection.events.map((e) => WEBHOOK_EVENT_LABELS[e]).join(', ')}</span>
        <span>
          {connection.lastDelivery === null
            ? 'Nothing sent yet'
            : `Last sent ${formatFullDateTime(connection.lastDelivery.createdAt)}: ${DELIVERY_LABELS[connection.lastDelivery.status].toLowerCase()}`}
        </span>
      </p>
      {revoked ? (
        <p className={styles.muted}>
          HubSpot revoked this connection. The app may have been removed from your HubSpot account,
          or the authorization expired. Remove it here and connect HubSpot again.
        </p>
      ) : null}

      {revoked ? null : (
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Sync to HubSpot</legend>
          {WEBHOOK_EVENT_TYPES.map((type) => {
            const checked = connection.events.includes(type);
            return (
              <label key={type} className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={update.isPending || (checked && connection.events.length === 1)}
                  onChange={(e) =>
                    update.mutate({
                      events: WEBHOOK_EVENT_TYPES.filter((t) =>
                        t === type ? e.target.checked : connection.events.includes(t),
                      ),
                    })
                  }
                />
                <span>{WEBHOOK_EVENT_LABELS[type]}</span>
              </label>
            );
          })}
        </fieldset>
      )}

      <div className={styles.actions}>
        {canSend && !revoked ? (
          <Button small onClick={() => test.mutate()} disabled={test.isPending}>
            {test.isPending ? 'Testing…' : 'Test connection'}
          </Button>
        ) : null}
        <Button
          small
          onClick={() => setShowDeliveries((v) => !v)}
          aria-expanded={showDeliveries}
          aria-controls={deliveriesId}
        >
          {showDeliveries ? 'Hide deliveries' : 'Deliveries'}
        </Button>
        {connection.isEnabled ? (
          <Button
            small
            variant="ghost"
            onClick={() => update.mutate({ isEnabled: false })}
            disabled={update.isPending}
          >
            Turn off
          </Button>
        ) : canSend && !revoked ? (
          <Button
            small
            variant="ghost"
            onClick={() => update.mutate({ isEnabled: true })}
            disabled={update.isPending}
          >
            Turn on
          </Button>
        ) : null}
        <Button small variant="ghost" onClick={() => setConfirmRemove(true)}>
          Remove
        </Button>
      </div>

      {test.data === undefined ? null : <CrmTestResult result={test.data} name="HubSpot" />}
      {failure === null ? null : (
        <p className={styles.error} role="alert">
          <CircleAlert aria-hidden="true" className={styles.errorIcon} strokeWidth={1.75} />
          {errorMessage(failure, "That didn't work. Check your connection and try again.")}
        </p>
      )}

      <div id={deliveriesId} hidden={!showDeliveries}>
        {showDeliveries ? <Deliveries websiteId={websiteId} endpointId={connection.id} /> : null}
      </div>

      <Dialog
        title="Remove this HubSpot connection?"
        isOpen={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        showCloseButton={false}
        footer={
          <>
            <Button autoFocus onClick={() => setConfirmRemove(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={remove.isPending}
              onClick={() => remove.mutate(undefined, { onSuccess: () => setConfirmRemove(false) })}
            >
              Remove connection
            </Button>
          </>
        }
      >
        <p>Leads stop going to HubSpot at once, and its delivery history is removed.</p>
      </Dialog>
    </li>
  );
}

function CrmTestResult({ result, name }: { result: WebhookTestResult; name: string }) {
  return result.ok ? (
    <p className={styles.success} role="status">
      <CircleCheck aria-hidden="true" className={styles.successIcon} strokeWidth={1.75} />
      Connected to {name}.
    </p>
  ) : (
    <p className={styles.error} role="alert">
      <CircleAlert aria-hidden="true" className={styles.errorIcon} strokeWidth={1.75} />
      {result.error ?? `${name} didn't respond.`}
    </p>
  );
}

function SalesforceView({ websiteId }: { websiteId: string }) {
  const connections = useSalesforceConnections(websiteId);
  const start = useStartSalesforceInstall(websiteId);
  const data = connections.data;
  const atLimit = data !== undefined && data.items.length >= data.limit;
  const isLeaving = start.isPending || start.isSuccess;

  return (
    <>
      <ViewHeader
        integration={integrationById('salesforce')}
        description="Groundly creates or updates a Salesforce lead for each new visitor: the name, email, phone, company, and a link to the conversation. If a lead with the same email already exists, it’s updated."
        action={
          data !== undefined && data.crmAvailable && data.integrationsAllowed && !atLimit ? (
            <Button variant="primary" onClick={() => start.mutate()} disabled={isLeaving}>
              {isLeaving ? 'Opening Salesforce…' : 'Connect Salesforce'}
            </Button>
          ) : null
        }
      />

      {data !== undefined && !data.integrationsAllowed ? <PlanNotice /> : null}

      {start.error === null ? null : (
        <p className={`${styles.error} ${styles.viewError}`} role="alert">
          <CircleAlert aria-hidden="true" className={styles.errorIcon} strokeWidth={1.75} />
          {errorMessage(
            start.error,
            'Salesforce didn’t open. Check your connection and try again.',
          )}
        </p>
      )}

      <div className={styles.viewBody}>
        <section className={styles.viewMain} aria-labelledby="salesforce-connections-heading">
          <h2 id="salesforce-connections-heading" className={styles.subTitle}>
            Connections
          </h2>
          {connections.isError ? (
            <p className={styles.error} role="alert">
              Salesforce connections didn&apos;t load.{' '}
              <Button small onClick={() => void connections.refetch()}>
                Try again
              </Button>
            </p>
          ) : data === undefined ? (
            <LoadingArea />
          ) : !data.crmAvailable && data.items.length === 0 ? (
            <p className={styles.muted}>Salesforce isn&apos;t set up on this Groundly yet.</p>
          ) : (
            <>
              {atLimit ? (
                <p className={styles.limit}>
                  A website can have {MAX_CRM_CONNECTIONS} Salesforce connection. Remove it to
                  connect a different account.
                </p>
              ) : null}
              {data.items.length === 0 ? (
                <div className={styles.empty}>
                  <h3>No Salesforce connection yet</h3>
                  <p>Connect Salesforce and every new lead becomes a lead record in your CRM.</p>
                </div>
              ) : (
                <ul className={styles.list}>
                  {data.items.map((connection) => (
                    <SalesforceRow
                      key={connection.id}
                      websiteId={websiteId}
                      connection={connection}
                      canSend={data.integrationsAllowed}
                    />
                  ))}
                </ul>
              )}
            </>
          )}
        </section>

        <Facts
          items={[
            {
              term: 'What’s synced',
              detail: 'Name, email, phone, company, and a link to the Groundly conversation.',
            },
            {
              term: 'When',
              detail: 'As each lead arrives. New conversations too, if you tick them.',
            },
            {
              term: 'Matching',
              detail:
                'Groundly matches by email. If the lead already exists, it is updated; otherwise a new one is created.',
            },
            {
              term: 'Limit',
              detail: 'One Salesforce connection per website.',
            },
          ]}
        />
      </div>
    </>
  );
}

function SalesforceRow({
  websiteId,
  connection,
  canSend,
}: {
  websiteId: string;
  connection: CrmConnection;
  canSend: boolean;
}) {
  const update = useUpdateSalesforceConnection(websiteId, connection.id);
  const remove = useDeleteSalesforceConnection(websiteId, connection.id);
  const test = useTestSalesforceConnection(websiteId, connection.id);
  const [showDeliveries, setShowDeliveries] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const deliveriesId = useId();

  const status = statusOf(connection);
  const revoked = connection.disabledReason === 'revoked';
  const failure = update.error ?? test.error;

  return (
    <li className={styles.item}>
      <div className={styles.top}>
        <span className={styles.url}>
          {connection.accountName || 'Salesforce'}{' '}
          {connection.portalUrl ? (
            <a
              href={connection.portalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.muted}
            >
              Open in Salesforce
            </a>
          ) : null}
        </span>
        <StatusBadge tone={status.tone} icon={status.icon}>
          {status.label}
        </StatusBadge>
      </div>
      <p className={styles.meta}>
        <span>Syncs: {connection.events.map((e) => WEBHOOK_EVENT_LABELS[e]).join(', ')}</span>
        <span>
          {connection.lastDelivery === null
            ? 'Nothing sent yet'
            : `Last sent ${formatFullDateTime(connection.lastDelivery.createdAt)}: ${DELIVERY_LABELS[connection.lastDelivery.status].toLowerCase()}`}
        </span>
      </p>
      {revoked ? (
        <p className={styles.muted}>
          Salesforce revoked this connection. The connected app may have been removed from your
          Salesforce org, or the authorization expired. Remove it here and connect Salesforce again.
        </p>
      ) : null}

      {revoked ? null : (
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Sync to Salesforce</legend>
          {WEBHOOK_EVENT_TYPES.map((type) => {
            const checked = connection.events.includes(type);
            return (
              <label key={type} className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={update.isPending || (checked && connection.events.length === 1)}
                  onChange={(e) =>
                    update.mutate({
                      events: WEBHOOK_EVENT_TYPES.filter((t) =>
                        t === type ? e.target.checked : connection.events.includes(t),
                      ),
                    })
                  }
                />
                <span>{WEBHOOK_EVENT_LABELS[type]}</span>
              </label>
            );
          })}
        </fieldset>
      )}

      <div className={styles.actions}>
        {canSend && !revoked ? (
          <Button small onClick={() => test.mutate()} disabled={test.isPending}>
            {test.isPending ? 'Testing…' : 'Test connection'}
          </Button>
        ) : null}
        <Button
          small
          onClick={() => setShowDeliveries((v) => !v)}
          aria-expanded={showDeliveries}
          aria-controls={deliveriesId}
        >
          {showDeliveries ? 'Hide deliveries' : 'Deliveries'}
        </Button>
        {connection.isEnabled ? (
          <Button
            small
            variant="ghost"
            onClick={() => update.mutate({ isEnabled: false })}
            disabled={update.isPending}
          >
            Turn off
          </Button>
        ) : canSend && !revoked ? (
          <Button
            small
            variant="ghost"
            onClick={() => update.mutate({ isEnabled: true })}
            disabled={update.isPending}
          >
            Turn on
          </Button>
        ) : null}
        <Button small variant="ghost" onClick={() => setConfirmRemove(true)}>
          Remove
        </Button>
      </div>

      {test.data === undefined ? null : <CrmTestResult result={test.data} name="Salesforce" />}
      {failure === null ? null : (
        <p className={styles.error} role="alert">
          <CircleAlert aria-hidden="true" className={styles.errorIcon} strokeWidth={1.75} />
          {errorMessage(failure, 'That didn’t work. Check your connection and try again.')}
        </p>
      )}

      <div id={deliveriesId} hidden={!showDeliveries}>
        {showDeliveries ? <Deliveries websiteId={websiteId} endpointId={connection.id} /> : null}
      </div>

      <Dialog
        title="Remove this Salesforce connection?"
        isOpen={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        showCloseButton={false}
        footer={
          <>
            <Button autoFocus onClick={() => setConfirmRemove(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={remove.isPending}
              onClick={() => remove.mutate(undefined, { onSuccess: () => setConfirmRemove(false) })}
            >
              Remove connection
            </Button>
          </>
        }
      >
        <p>Leads stop going to Salesforce at once, and its delivery history is removed.</p>
      </Dialog>
    </li>
  );
}

function ZohoView({ websiteId }: { websiteId: string }) {
  const connections = useZohoConnections(websiteId);
  const start = useStartZohoInstall(websiteId);
  const data = connections.data;
  const atLimit = data !== undefined && data.items.length >= data.limit;
  const isLeaving = start.isPending || start.isSuccess;

  return (
    <>
      <ViewHeader
        integration={integrationById('zoho')}
        description="Groundly creates or updates a Zoho CRM lead for each new visitor: the name, email, phone, company, and a link to the conversation. If a lead with the same email already exists, it’s updated."
        action={
          data !== undefined && data.crmAvailable && data.integrationsAllowed && !atLimit ? (
            <Button variant="primary" onClick={() => start.mutate()} disabled={isLeaving}>
              {isLeaving ? 'Opening Zoho…' : 'Connect Zoho CRM'}
            </Button>
          ) : null
        }
      />

      {data !== undefined && !data.integrationsAllowed ? <PlanNotice /> : null}

      {start.error === null ? null : (
        <p className={`${styles.error} ${styles.viewError}`} role="alert">
          <CircleAlert aria-hidden="true" className={styles.errorIcon} strokeWidth={1.75} />
          {errorMessage(start.error, 'Zoho didn’t open. Check your connection and try again.')}
        </p>
      )}

      <div className={styles.viewBody}>
        <section className={styles.viewMain} aria-labelledby="zoho-connections-heading">
          <h2 id="zoho-connections-heading" className={styles.subTitle}>
            Connections
          </h2>
          {connections.isError ? (
            <p className={styles.error} role="alert">
              Zoho CRM connections didn&apos;t load.{' '}
              <Button small onClick={() => void connections.refetch()}>
                Try again
              </Button>
            </p>
          ) : data === undefined ? (
            <LoadingArea />
          ) : !data.crmAvailable && data.items.length === 0 ? (
            <p className={styles.muted}>Zoho CRM isn&apos;t set up on this Groundly yet.</p>
          ) : (
            <>
              {atLimit ? (
                <p className={styles.limit}>
                  A website can have {MAX_CRM_CONNECTIONS} Zoho CRM connection. Remove it to connect
                  a different account.
                </p>
              ) : null}
              {data.items.length === 0 ? (
                <div className={styles.empty}>
                  <h3>No Zoho CRM connection yet</h3>
                  <p>Connect Zoho CRM and every new lead becomes a lead record in your CRM.</p>
                </div>
              ) : (
                <ul className={styles.list}>
                  {data.items.map((connection) => (
                    <ZohoRow
                      key={connection.id}
                      websiteId={websiteId}
                      connection={connection}
                      canSend={data.integrationsAllowed}
                    />
                  ))}
                </ul>
              )}
            </>
          )}
        </section>

        <Facts
          items={[
            {
              term: 'What’s synced',
              detail: 'Name, email, phone, company, and a link to the Groundly conversation.',
            },
            {
              term: 'When',
              detail: 'As each lead arrives. New conversations too, if you tick them.',
            },
            {
              term: 'Matching',
              detail:
                'Groundly matches by email. If the lead already exists, it is updated; otherwise a new one is created.',
            },
            {
              term: 'Limit',
              detail: 'One Zoho CRM connection per website.',
            },
          ]}
        />
      </div>
    </>
  );
}

function ZohoRow({
  websiteId,
  connection,
  canSend,
}: {
  websiteId: string;
  connection: CrmConnection;
  canSend: boolean;
}) {
  const update = useUpdateZohoConnection(websiteId, connection.id);
  const remove = useDeleteZohoConnection(websiteId, connection.id);
  const test = useTestZohoConnection(websiteId, connection.id);
  const [showDeliveries, setShowDeliveries] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const deliveriesId = useId();

  const status = statusOf(connection);
  const revoked = connection.disabledReason === 'revoked';
  const failure = update.error ?? test.error;

  return (
    <li className={styles.item}>
      <div className={styles.top}>
        <span className={styles.url}>
          {connection.accountName || 'Zoho CRM'}{' '}
          {connection.portalUrl ? (
            <a
              href={connection.portalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.muted}
            >
              Open in Zoho CRM
            </a>
          ) : null}
        </span>
        <StatusBadge tone={status.tone} icon={status.icon}>
          {status.label}
        </StatusBadge>
      </div>
      <p className={styles.meta}>
        <span>Syncs: {connection.events.map((e) => WEBHOOK_EVENT_LABELS[e]).join(', ')}</span>
        <span>
          {connection.lastDelivery === null
            ? 'Nothing sent yet'
            : `Last sent ${formatFullDateTime(connection.lastDelivery.createdAt)}: ${DELIVERY_LABELS[connection.lastDelivery.status].toLowerCase()}`}
        </span>
      </p>
      {revoked ? (
        <p className={styles.muted}>
          Zoho CRM revoked this connection. The app may have been removed from your Zoho account, or
          the authorization expired. Remove it here and connect Zoho CRM again.
        </p>
      ) : null}

      {revoked ? null : (
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Sync to Zoho CRM</legend>
          {WEBHOOK_EVENT_TYPES.map((type) => {
            const checked = connection.events.includes(type);
            return (
              <label key={type} className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={update.isPending || (checked && connection.events.length === 1)}
                  onChange={(e) =>
                    update.mutate({
                      events: WEBHOOK_EVENT_TYPES.filter((t) =>
                        t === type ? e.target.checked : connection.events.includes(t),
                      ),
                    })
                  }
                />
                <span>{WEBHOOK_EVENT_LABELS[type]}</span>
              </label>
            );
          })}
        </fieldset>
      )}

      <div className={styles.actions}>
        {canSend && !revoked ? (
          <Button small onClick={() => test.mutate()} disabled={test.isPending}>
            {test.isPending ? 'Testing…' : 'Test connection'}
          </Button>
        ) : null}
        <Button
          small
          onClick={() => setShowDeliveries((v) => !v)}
          aria-expanded={showDeliveries}
          aria-controls={deliveriesId}
        >
          {showDeliveries ? 'Hide deliveries' : 'Deliveries'}
        </Button>
        {connection.isEnabled ? (
          <Button
            small
            variant="ghost"
            onClick={() => update.mutate({ isEnabled: false })}
            disabled={update.isPending}
          >
            Turn off
          </Button>
        ) : canSend && !revoked ? (
          <Button
            small
            variant="ghost"
            onClick={() => update.mutate({ isEnabled: true })}
            disabled={update.isPending}
          >
            Turn on
          </Button>
        ) : null}
        <Button small variant="ghost" onClick={() => setConfirmRemove(true)}>
          Remove
        </Button>
      </div>

      {test.data === undefined ? null : <CrmTestResult result={test.data} name="Zoho CRM" />}
      {failure === null ? null : (
        <p className={styles.error} role="alert">
          <CircleAlert aria-hidden="true" className={styles.errorIcon} strokeWidth={1.75} />
          {errorMessage(failure, 'That didn’t work. Check your connection and try again.')}
        </p>
      )}

      <div id={deliveriesId} hidden={!showDeliveries}>
        {showDeliveries ? <Deliveries websiteId={websiteId} endpointId={connection.id} /> : null}
      </div>

      <Dialog
        title="Remove this Zoho CRM connection?"
        isOpen={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        showCloseButton={false}
        footer={
          <>
            <Button autoFocus onClick={() => setConfirmRemove(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={remove.isPending}
              onClick={() => remove.mutate(undefined, { onSuccess: () => setConfirmRemove(false) })}
            >
              Remove connection
            </Button>
          </>
        }
      >
        <p>Leads stop going to Zoho CRM at once, and its delivery history is removed.</p>
      </Dialog>
    </li>
  );
}

function WebhooksView({ websiteId }: { websiteId: string }) {
  const endpoints = useWebhookEndpoints(websiteId);
  const [isAdding, setIsAdding] = useState(false);
  const [revealed, setRevealed] = useState<WebhookEndpointWithSecret | null>(null);
  const data = endpoints.data;
  const atLimit = data !== undefined && data.items.length >= data.limit;

  return (
    <>
      <ViewHeader
        integration={integrationById('webhooks')}
        description="Groundly sends each new lead, and if you choose each new conversation, to your address as a signed JSON request: the visitor’s details and the conversation. Use it with your own system, or any tool that can receive a webhook."
        action={
          data !== undefined && data.integrationsAllowed && !atLimit ? (
            <Button variant="primary" onClick={() => setIsAdding(true)}>
              Add webhook
            </Button>
          ) : null
        }
      />

      {endpoints.isError ? (
        <DangerAlert
          title="Webhooks didn't load"
          action={<Button onClick={() => void endpoints.refetch()}>Try again</Button>}
        >
          Groundly couldn&apos;t reach the server. Check your connection and try again.
        </DangerAlert>
      ) : data === undefined ? (
        <LoadingArea />
      ) : (
        <>
          {data.integrationsAllowed ? null : <PlanNotice />}
          <div className={styles.viewBody}>
            <section className={styles.viewMain} aria-labelledby="webhooks-heading">
              <h2 id="webhooks-heading" className={styles.subTitle}>
                Your webhooks
              </h2>
              <WebhookList
                websiteId={websiteId}
                endpoints={data.items}
                canSend={data.integrationsAllowed}
                atLimit={atLimit}
                onSecret={setRevealed}
                empty={
                  <div className={styles.empty}>
                    <h3>No webhooks yet</h3>
                    <p>Add one to get every new lead in your CRM, chat tool or spreadsheet.</p>
                  </div>
                }
              />
              <VerifyHelp />
            </section>

            <Facts
              items={[
                {
                  term: 'What’s sent',
                  detail:
                    'A POST with a JSON body: the event, the visitor’s details and the conversation.',
                },
                {
                  term: 'Signed',
                  detail: (
                    <>
                      Each request carries a <code>{WEBHOOK_HEADERS.signature}</code> header made
                      with the webhook’s signing secret.
                    </>
                  ),
                },
                {
                  term: 'Retries',
                  detail:
                    'Anything but a 2xx within 10 seconds is tried again, for up to 24 hours. A webhook that fails 5 leads in a row is turned off.',
                },
                {
                  term: 'Limit',
                  detail: `Up to ${MAX_WEBHOOK_ENDPOINTS} webhooks per website.`,
                },
              ]}
            />
          </div>
        </>
      )}

      <AddWebhookDialog
        websiteId={websiteId}
        isOpen={isAdding}
        onClose={() => setIsAdding(false)}
        onCreated={(created) => {
          setIsAdding(false);
          setRevealed(created);
        }}
      />
      <SecretDialog endpoint={revealed} onClose={() => setRevealed(null)} />
    </>
  );
}

function WebhookList({
  websiteId,
  endpoints,
  canSend,
  atLimit,
  onSecret,
  empty,
}: {
  websiteId: string;
  endpoints: readonly WebhookEndpoint[];
  canSend: boolean;
  atLimit: boolean;
  onSecret: (endpoint: WebhookEndpointWithSecret) => void;
  empty: ReactNode;
}) {
  return (
    <>
      {atLimit ? (
        <p className={styles.limit}>
          A website can have up to {MAX_WEBHOOK_ENDPOINTS} webhooks. Remove one to add another.
        </p>
      ) : null}
      {endpoints.length === 0 ? (
        empty
      ) : (
        <ul className={styles.list}>
          {endpoints.map((endpoint) => (
            <EndpointRow
              key={endpoint.id}
              websiteId={websiteId}
              endpoint={endpoint}
              canSend={canSend}
              onSecret={onSecret}
            />
          ))}
        </ul>
      )}
    </>
  );
}

/**
 * A tool that works through a webhook (Zapier, Make, n8n): how to get its
 * webhook address, an "Add webhook" that asks for it, and the webhooks
 * already going to that tool.
 */
function WebhookToolView({ websiteId, tool }: { websiteId: string; tool: WebhookTool }) {
  const integration = integrationById(tool);
  const guide = WEBHOOK_GUIDES[tool];
  const endpoints = useWebhookEndpoints(websiteId);
  const [isAdding, setIsAdding] = useState(false);
  const [revealed, setRevealed] = useState<WebhookEndpointWithSecret | null>(null);
  const data = endpoints.data;
  const atLimit = data !== undefined && data.items.length >= data.limit;
  const mine = data?.items.filter((e) => toolForUrl(e.url) === tool) ?? [];

  return (
    <>
      <ViewHeader
        integration={integration}
        description={`${integration.summary} Groundly sends each lead to ${integration.name} through a webhook, the moment it arrives; ${integration.name} takes it from there.`}
        action={
          data !== undefined && data.integrationsAllowed && !atLimit ? (
            <Button variant="primary" onClick={() => setIsAdding(true)}>
              Add webhook
            </Button>
          ) : null
        }
      />

      {endpoints.isError ? (
        <DangerAlert
          title="Webhooks didn't load"
          action={<Button onClick={() => void endpoints.refetch()}>Try again</Button>}
        >
          Groundly couldn&apos;t reach the server. Check your connection and try again.
        </DangerAlert>
      ) : data === undefined ? (
        <LoadingArea />
      ) : (
        <>
          {data.integrationsAllowed ? null : <PlanNotice />}
          <div className={styles.viewBody}>
            <div className={styles.viewMain}>
              <section aria-labelledby="setup-heading" className={styles.setup}>
                <h2 id="setup-heading" className={styles.subTitle}>
                  Set it up
                </h2>
                <ol className={styles.steps}>
                  {guide.steps.map((step) => (
                    <li key={step} className={styles.step}>
                      {step}
                    </li>
                  ))}
                </ol>
              </section>

              <section aria-labelledby="tool-webhooks-heading">
                <h2 id="tool-webhooks-heading" className={styles.subTitle}>
                  Webhooks going to {integration.name}
                </h2>
                <WebhookList
                  websiteId={websiteId}
                  endpoints={mine}
                  canSend={data.integrationsAllowed}
                  atLimit={atLimit}
                  onSecret={setRevealed}
                  empty={
                    <div className={styles.empty}>
                      <h3>None yet</h3>
                      <p>
                        Once you add a webhook with a {integration.name} address, it&apos;s listed
                        here.
                      </p>
                    </div>
                  }
                />
              </section>
            </div>

            <Facts
              items={[
                {
                  term: 'What you need',
                  detail: `A ${integration.name} account that can receive webhooks. Groundly sends to it like any other webhook.`,
                },
                {
                  term: 'What’s sent',
                  detail:
                    'The event, the visitor’s details and the conversation, as JSON fields you can map to any step.',
                },
                {
                  term: 'All webhooks',
                  detail: (
                    <Link
                      to="/websites/$websiteId/integrations"
                      params={{ websiteId }}
                      search={{ integration: 'webhooks' }}
                    >
                      See every webhook on this website
                    </Link>
                  ),
                },
              ]}
            />
          </div>
        </>
      )}

      <AddWebhookDialog
        websiteId={websiteId}
        isOpen={isAdding}
        placeholder={guide.placeholder}
        defaultDescription={integration.name}
        onClose={() => setIsAdding(false)}
        onCreated={(created) => {
          setIsAdding(false);
          setRevealed(created);
        }}
      />
      <SecretDialog endpoint={revealed} onClose={() => setRevealed(null)} />
    </>
  );
}

function SlackRow({
  websiteId,
  connection,
  canSend,
}: {
  websiteId: string;
  connection: SlackConnection;
  canSend: boolean;
}) {
  const update = useUpdateSlackConnection(websiteId, connection.id);
  const remove = useDeleteSlackConnection(websiteId, connection.id);
  const test = useTestSlackConnection(websiteId, connection.id);
  const [showDeliveries, setShowDeliveries] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const deliveriesId = useId();

  const status = statusOf(connection);
  const revoked = connection.disabledReason === 'revoked';
  const failure = update.error ?? test.error;

  return (
    <li className={styles.item}>
      <div className={styles.top}>
        <span className={styles.url}>
          #{connection.channelName} <span className={styles.muted}>in {connection.teamName}</span>
        </span>
        <StatusBadge tone={status.tone} icon={status.icon}>
          {status.label}
        </StatusBadge>
      </div>
      <p className={styles.meta}>
        <span>Posts: {connection.events.map((e) => WEBHOOK_EVENT_LABELS[e]).join(', ')}</span>
        <span>
          {connection.lastDelivery === null
            ? 'Nothing posted yet'
            : `Last posted ${formatFullDateTime(connection.lastDelivery.createdAt)}: ${DELIVERY_LABELS[connection.lastDelivery.status].toLowerCase()}`}
        </span>
      </p>
      {revoked ? (
        <p className={styles.muted}>
          Slack removed this connection: the app was taken out of the workspace, or the channel was
          archived or deleted. Remove it here and add the channel again.
        </p>
      ) : null}

      {revoked ? null : (
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Post to this channel</legend>
          {WEBHOOK_EVENT_TYPES.map((type) => {
            const checked = connection.events.includes(type);
            return (
              <label key={type} className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={checked}
                  // At least one event: the last one ticked can't be unticked.
                  disabled={update.isPending || (checked && connection.events.length === 1)}
                  onChange={(e) =>
                    update.mutate({
                      events: WEBHOOK_EVENT_TYPES.filter((t) =>
                        t === type ? e.target.checked : connection.events.includes(t),
                      ),
                    })
                  }
                />
                <span>{WEBHOOK_EVENT_LABELS[type]}</span>
              </label>
            );
          })}
        </fieldset>
      )}

      <div className={styles.actions}>
        {canSend && !revoked ? (
          <Button small onClick={() => test.mutate()} disabled={test.isPending}>
            {test.isPending ? 'Posting…' : 'Post test message'}
          </Button>
        ) : null}
        <Button
          small
          onClick={() => setShowDeliveries((v) => !v)}
          aria-expanded={showDeliveries}
          aria-controls={deliveriesId}
        >
          {showDeliveries ? 'Hide deliveries' : 'Deliveries'}
        </Button>
        {connection.isEnabled ? (
          <Button
            small
            variant="ghost"
            onClick={() => update.mutate({ isEnabled: false })}
            disabled={update.isPending}
          >
            Turn off
          </Button>
        ) : canSend && !revoked ? (
          <Button
            small
            variant="ghost"
            onClick={() => update.mutate({ isEnabled: true })}
            disabled={update.isPending}
          >
            Turn on
          </Button>
        ) : null}
        <Button small variant="ghost" onClick={() => setConfirmRemove(true)}>
          Remove
        </Button>
      </div>

      {test.data === undefined ? null : <SlackTestResult result={test.data} />}
      {failure === null ? null : (
        <p className={styles.error} role="alert">
          <CircleAlert aria-hidden="true" className={styles.errorIcon} strokeWidth={1.75} />
          {errorMessage(failure, "That didn't work. Check your connection and try again.")}
        </p>
      )}

      <div id={deliveriesId} hidden={!showDeliveries}>
        {showDeliveries ? <Deliveries websiteId={websiteId} endpointId={connection.id} /> : null}
      </div>

      <Dialog
        title="Remove this Slack channel?"
        isOpen={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        showCloseButton={false}
        footer={
          <>
            <Button autoFocus onClick={() => setConfirmRemove(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={remove.isPending}
              onClick={() => remove.mutate(undefined, { onSuccess: () => setConfirmRemove(false) })}
            >
              Remove channel
            </Button>
          </>
        }
      >
        <p>
          Leads stop going to #{connection.channelName} at once, and its delivery history is
          removed. To take Groundly out of your Slack workspace too, remove the app in Slack.
        </p>
      </Dialog>
    </li>
  );
}

function SlackTestResult({ result }: { result: WebhookTestResult }) {
  return result.ok ? (
    <p className={styles.success} role="status">
      <CircleCheck aria-hidden="true" className={styles.successIcon} strokeWidth={1.75} />
      Test message posted. Check the channel in Slack.
    </p>
  ) : (
    <p className={styles.error} role="alert">
      <CircleAlert aria-hidden="true" className={styles.errorIcon} strokeWidth={1.75} />
      Test message not posted. {result.error}
    </p>
  );
}

function EndpointRow({
  websiteId,
  endpoint,
  canSend,
  onSecret,
}: {
  websiteId: string;
  endpoint: WebhookEndpoint;
  canSend: boolean;
  onSecret: (endpoint: WebhookEndpointWithSecret) => void;
}) {
  const update = useUpdateWebhookEndpoint(websiteId, endpoint.id);
  const remove = useDeleteWebhookEndpoint(websiteId, endpoint.id);
  const rotate = useRotateWebhookSecret(websiteId, endpoint.id);
  const test = useTestWebhookEndpoint(websiteId, endpoint.id);
  const [showDeliveries, setShowDeliveries] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const deliveriesId = useId();

  const state = endpoint.isEnabled
    ? 'on'
    : endpoint.disabledReason === 'failing'
      ? 'failing'
      : 'off';
  const status = STATUS[state];
  const failure = update.error ?? rotate.error ?? test.error;

  return (
    <li className={styles.item}>
      <div className={styles.top}>
        <code className={styles.url}>{endpoint.url}</code>
        <StatusBadge tone={status.tone} icon={status.icon}>
          {status.label}
        </StatusBadge>
      </div>
      {endpoint.description === null ? null : (
        <p className={styles.description}>{endpoint.description}</p>
      )}
      <p className={styles.meta}>
        <span>Sends: {endpoint.events.map((e) => WEBHOOK_EVENT_LABELS[e]).join(', ')}</span>
        <span>
          Secret: <code>{endpoint.secretHint}…</code>
        </span>
        <span>
          {endpoint.lastDelivery === null
            ? 'Nothing sent yet'
            : `Last sent ${formatFullDateTime(endpoint.lastDelivery.createdAt)}: ${DELIVERY_LABELS[endpoint.lastDelivery.status].toLowerCase()}`}
        </span>
      </p>

      <div className={styles.actions}>
        {canSend ? (
          <Button small onClick={() => test.mutate()} disabled={test.isPending}>
            {test.isPending ? 'Sending…' : 'Send test event'}
          </Button>
        ) : null}
        <Button
          small
          onClick={() => setShowDeliveries((v) => !v)}
          aria-expanded={showDeliveries}
          aria-controls={deliveriesId}
        >
          {showDeliveries ? 'Hide deliveries' : 'Deliveries'}
        </Button>
        {endpoint.isEnabled ? (
          <Button
            small
            variant="ghost"
            onClick={() => update.mutate({ isEnabled: false })}
            disabled={update.isPending}
          >
            Turn off
          </Button>
        ) : canSend ? (
          <Button
            small
            variant="ghost"
            onClick={() => update.mutate({ isEnabled: true })}
            disabled={update.isPending}
          >
            Turn on
          </Button>
        ) : null}
        <Button small variant="ghost" onClick={() => setConfirmRotate(true)}>
          New secret
        </Button>
        <Button small variant="ghost" onClick={() => setConfirmRemove(true)}>
          Remove
        </Button>
      </div>

      {test.data === undefined ? null : <TestResult result={test.data} />}
      {failure === null ? null : (
        <p className={styles.error} role="alert">
          <CircleAlert aria-hidden="true" className={styles.errorIcon} strokeWidth={1.75} />
          {errorMessage(failure, "That didn't work. Check your connection and try again.")}
        </p>
      )}

      <div id={deliveriesId} hidden={!showDeliveries}>
        {showDeliveries ? <Deliveries websiteId={websiteId} endpointId={endpoint.id} /> : null}
      </div>

      <Dialog
        title="Remove this webhook?"
        isOpen={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        showCloseButton={false}
        footer={
          <>
            <Button autoFocus onClick={() => setConfirmRemove(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={remove.isPending}
              onClick={() => remove.mutate(undefined, { onSuccess: () => setConfirmRemove(false) })}
            >
              Remove webhook
            </Button>
          </>
        }
      >
        <p>
          Leads stop going to <code className={styles.url}>{endpoint.url}</code> at once, and its
          delivery history is removed.
        </p>
      </Dialog>

      <Dialog
        title="Make a new signing secret?"
        isOpen={confirmRotate}
        onClose={() => setConfirmRotate(false)}
        showCloseButton={false}
        footer={
          <>
            <Button autoFocus onClick={() => setConfirmRotate(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={rotate.isPending}
              onClick={() =>
                rotate.mutate(undefined, {
                  onSuccess: (rotated) => {
                    setConfirmRotate(false);
                    onSecret(rotated);
                  },
                })
              }
            >
              Make new secret
            </Button>
          </>
        }
      >
        <p>
          The current secret stops working straight away. Requests signed with the new one fail your
          check until you update it in your system.
        </p>
      </Dialog>
    </li>
  );
}

function TestResult({ result }: { result: WebhookTestResult }) {
  return result.ok ? (
    <p className={styles.success} role="status">
      <CircleCheck aria-hidden="true" className={styles.successIcon} strokeWidth={1.75} />
      Test event delivered: your endpoint answered {result.statusCode} in {result.durationMs} ms.
    </p>
  ) : (
    <p className={styles.error} role="alert">
      <CircleAlert aria-hidden="true" className={styles.errorIcon} strokeWidth={1.75} />
      Test event not delivered. {result.error}
    </p>
  );
}

const DELIVERY_LABELS: Record<WebhookDeliveryStatus, string> = {
  pending: 'Retrying',
  succeeded: 'Delivered',
  failed: 'Failed',
  skipped: 'Not sent',
};

const DELIVERY_BADGES: Record<
  WebhookDeliveryStatus,
  { tone: 'success' | 'neutral' | 'attention' | 'danger'; icon: LucideIcon }
> = {
  pending: { tone: 'attention', icon: CircleDashed },
  succeeded: { tone: 'success', icon: CircleCheck },
  failed: { tone: 'danger', icon: CircleX },
  skipped: { tone: 'neutral', icon: CirclePause },
};

function Deliveries({ websiteId, endpointId }: { websiteId: string; endpointId: string }) {
  const [page, setPage] = useState(1);
  const deliveries = useWebhookDeliveries(websiteId, endpointId, page);
  const redeliver = useRedeliverWebhook(websiteId, endpointId);

  if (deliveries.isError) {
    return (
      <p className={styles.error} role="alert">
        Deliveries didn&apos;t load.{' '}
        <Button small onClick={() => void deliveries.refetch()}>
          Try again
        </Button>
      </p>
    );
  }
  if (deliveries.data === undefined) return <LoadingArea />;
  if (deliveries.data.total === 0) {
    return <p className={styles.muted}>No leads have been sent here yet.</p>;
  }
  return (
    <div className={styles.deliveries}>
      <table className={styles.table}>
        <caption className={styles.visuallyHidden}>Deliveries, newest first</caption>
        <thead>
          <tr>
            <th scope="col">Event</th>
            <th scope="col">Status</th>
            <th scope="col">Last attempt</th>
            <th scope="col">
              <span className={styles.visuallyHidden}>Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {deliveries.data.items.map((d) => (
            <DeliveryRow
              key={d.id}
              delivery={d}
              onRedeliver={() => redeliver.mutate(d.id)}
              isBusy={redeliver.isPending && redeliver.variables === d.id}
            />
          ))}
        </tbody>
      </table>
      {redeliver.error === null ? null : (
        <p className={styles.error} role="alert">
          {errorMessage(redeliver.error, "That didn't work. Try again.")}
        </p>
      )}
      <Pagination
        page={deliveries.data.page}
        pageSize={deliveries.data.pageSize}
        total={deliveries.data.total}
        onPageChange={setPage}
      />
    </div>
  );
}

function DeliveryRow({
  delivery,
  onRedeliver,
  isBusy,
}: {
  delivery: WebhookDelivery;
  onRedeliver: () => void;
  isBusy: boolean;
}) {
  const badge = DELIVERY_BADGES[delivery.status];
  const attempt =
    delivery.attemptCount === 0
      ? 'Not tried yet'
      : [
          `Attempt ${delivery.attemptCount}`,
          delivery.lastStatusCode === null ? null : `answered ${delivery.lastStatusCode}`,
          delivery.lastError,
        ]
          .filter(Boolean)
          .join(' · ');
  return (
    <tr>
      <td>
        {WEBHOOK_EVENT_LABELS[delivery.eventType]}
        <br />
        <time className={styles.muted} dateTime={delivery.createdAt}>
          {formatFullDateTime(delivery.createdAt)}
        </time>
      </td>
      <td>
        <StatusBadge tone={badge.tone} icon={badge.icon}>
          {DELIVERY_LABELS[delivery.status]}
        </StatusBadge>
      </td>
      <td className={styles.attempt}>
        {attempt}
        {delivery.nextAttemptAt === null ? null : (
          <>
            <br />
            <span className={styles.muted}>
              Next try {formatFullDateTime(delivery.nextAttemptAt)}
            </span>
          </>
        )}
      </td>
      <td>
        {delivery.status === 'pending' ? null : (
          <Button small variant="ghost" onClick={onRedeliver} disabled={isBusy}>
            Send again
          </Button>
        )}
      </td>
    </tr>
  );
}

function AddWebhookDialog({
  websiteId,
  isOpen,
  placeholder = 'https://hooks.zapier.com/…',
  defaultDescription = '',
  onClose,
  onCreated,
}: {
  websiteId: string;
  isOpen: boolean;
  /** An example address from the tool the user is connecting. */
  placeholder?: string;
  /** Prefills the description, e.g. "Zapier" from Zapier's view. */
  defaultDescription?: string;
  onClose: () => void;
  onCreated: (endpoint: WebhookEndpointWithSecret) => void;
}) {
  const create = useCreateWebhookEndpoint(websiteId);
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState(defaultDescription);
  const [events, setEvents] = useState<Set<WebhookEventType>>(new Set(LEAD_EVENT_TYPES));
  const [errors, setErrors] = useState<{ url?: string; events?: string }>({});

  const close = () => {
    setUrl('');
    setDescription(defaultDescription);
    setEvents(new Set(LEAD_EVENT_TYPES));
    setErrors({});
    create.reset();
    onClose();
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = createWebhookEndpointBodySchema.safeParse({
      url,
      description,
      events: [...events],
    });
    if (!parsed.success) {
      const found: { url?: string; events?: string } = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if (field === 'url' && found.url === undefined) found.url = issue.message;
        if (field === 'events' && found.events === undefined) found.events = issue.message;
      }
      setErrors(found);
      return;
    }
    setErrors({});
    create.mutate(parsed.data, {
      onSuccess: (created) => {
        setUrl('');
        setDescription(defaultDescription);
        setEvents(new Set(LEAD_EVENT_TYPES));
        onCreated(created);
      },
    });
  };

  return (
    <Dialog
      title="Add webhook"
      isOpen={isOpen}
      onClose={close}
      onSubmit={submit}
      footer={
        <>
          <Button onClick={close}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {create.isPending ? 'Adding…' : 'Add webhook'}
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <TextField
          id="webhook-url"
          label="Endpoint address"
          type="url"
          inputMode="url"
          autoComplete="off"
          placeholder={placeholder}
          autoFocus
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          hint="Where Groundly sends each lead. It must start with https://."
          error={errors.url}
        />
        <TextField
          id="webhook-description"
          label="Description (optional)"
          maxLength={200}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          hint="To tell your webhooks apart, e.g. “HubSpot via Zapier”."
        />
        <fieldset
          className={styles.fieldset}
          aria-describedby={errors.events ? 'events-error' : undefined}
        >
          <legend className={styles.legend}>Events to send</legend>
          {WEBHOOK_EVENT_TYPES.map((type) => (
            <label key={type} className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={events.has(type)}
                onChange={(e) => {
                  const next = new Set(events);
                  if (e.target.checked) next.add(type);
                  else next.delete(type);
                  setEvents(next);
                }}
              />
              <span>
                {WEBHOOK_EVENT_LABELS[type]} <code className={styles.muted}>{type}</code>
              </span>
            </label>
          ))}
          {errors.events === undefined ? null : (
            <p id="events-error" className={styles.error}>
              <CircleAlert aria-hidden="true" className={styles.errorIcon} strokeWidth={1.75} />
              {errors.events}
            </p>
          )}
        </fieldset>
        {create.error === null ? null : (
          <p className={styles.error} role="alert">
            <CircleAlert aria-hidden="true" className={styles.errorIcon} strokeWidth={1.75} />
            {errorMessage(
              create.error,
              "The webhook wasn't added. Check your connection and try again.",
            )}
          </p>
        )}
      </div>
    </Dialog>
  );
}

function SecretDialog({
  endpoint,
  onClose,
}: {
  endpoint: WebhookEndpointWithSecret | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const close = () => {
    setCopied(false);
    onClose();
  };
  return (
    <Dialog
      title="Copy your signing secret"
      isOpen={endpoint !== null}
      onClose={close}
      footer={
        <Button variant="primary" autoFocus onClick={close}>
          Done
        </Button>
      }
    >
      <p>
        Your system uses this secret to check each request came from Groundly. It&apos;s shown only
        now: store it somewhere safe. If you lose it, make a new one.
      </p>
      <div className={styles.secretRow}>
        <code className={styles.secret} aria-label="Signing secret">
          {endpoint?.secret}
        </code>
        <Button
          small
          onClick={() => {
            if (endpoint === null) return;
            void navigator.clipboard.writeText(endpoint.secret).then(() => setCopied(true));
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <p className={styles.muted} role="status">
        {copied ? 'The secret is on your clipboard.' : ''}
      </p>
    </Dialog>
  );
}

function VerifyHelp() {
  return (
    <details className={styles.help}>
      <summary>How to check a request came from Groundly</summary>
      <p>
        Every request has a <code>{WEBHOOK_HEADERS.signature}</code> header:{' '}
        <code>t=&lt;timestamp&gt;,v1=&lt;signature&gt;</code>. Compute an HMAC-SHA256 of{' '}
        <code>&lt;timestamp&gt;.&lt;raw request body&gt;</code> with your signing secret, compare it
        with <code>v1</code>, and refuse a timestamp more than 5 minutes old.
      </p>
      <pre className={styles.code}>{`import crypto from 'node:crypto';

function isFromGroundly(rawBody, header, secret) {
  const parts = Object.fromEntries(header.split(',').map((p) => p.split('=')));
  if (Math.abs(Date.now() / 1000 - Number(parts.t)) > 300) return false;
  const expected = Buffer.from(crypto.createHmac('sha256', secret)
    .update(\`\${parts.t}.\${rawBody}\`).digest('hex'));
  const given = Buffer.from(parts.v1 ?? '');
  return given.length === expected.length && crypto.timingSafeEqual(given, expected);
}`}</pre>
      <p>
        Answer with any 2xx status within 10 seconds. Anything else is tried again: after 1 minute,
        5 minutes, 30 minutes, 2, 6, 12 and 24 hours. The same lead keeps its{' '}
        <code>{WEBHOOK_HEADERS.eventId}</code>, so use it to ignore repeats. A webhook that fails 5
        leads in a row is turned off, and you can turn it back on here.
      </p>
    </details>
  );
}
